import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from './api.ts';
import { knownRegion, phonePlaceholder, phoneRegionOptions } from './lib/phone-regions.ts';
import { Button, Field, Finding, Select } from './ui.tsx';

/**
 * The work mobile (migration 0138, Trav 2026-09-30).
 *
 * Owners, administrators and staff carry a mobile the organisation can CALL —
 * the authorise sheet rings the person who raised the alert — and it counts as
 * theirs once they type back a code texted to it. It is asked for by a banner
 * that never covers anything: the ruling makes it mandatory, and the banner is
 * how it stays mandatory without ever standing between a person and an alert,
 * the raise flow, or an authorisation waiting for them.
 */

/**
 * Why the number is asked for, in words that fit the person's role (Trav,
 * 2026-10-02). Never assume a separate administrator exists: the person
 * creating a workplace IS its administrator, and a one-admin organisation has
 * nobody else to ring them.
 */
export function workMobileWhy(role?: string | null): string {
  if (role === 'owner' || role === 'admin') {
    return 'So another administrator can call you to confirm a real alert, and so you can be reached when one needs your approval.';
  }
  if (role === 'staff' || role === 'viewer') {
    return 'So your administrators can reach you during an alert.';
  }
  return 'So your organisation can reach you during an alert.';
}

const CODE_SENTENCE =
  "We'll text one code to check it's yours. After that we only call it — we won't text it unless your organisation signs you up for SMS alerts.";

/** The field hint: why, then what happens to the number. */
export function workMobileHelp(role?: string | null): string {
  return `${workMobileWhy(role)} ${CODE_SENTENCE}`;
}

/** Session-only "not now" for the banner — never persisted. */
const NOT_NOW_KEY = 'evac.work-mobile.not-now';

export function workMobileSnoozed(): boolean {
  try {
    return sessionStorage.getItem(NOT_NOW_KEY) === '1';
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    sessionStorage.setItem(NOT_NOW_KEY, '1');
  } catch {
    // Applies to this render only; it will ask again on the next load.
  }
}

/** A refusal of the number itself — fixed by typing or picking another. */
function isNumberRefusal(err: unknown): boolean {
  const code = api.errorCode(err);
  return code === 'invalid_mobile' || code === 'country_not_supported';
}

/** A refusal the person can fix by typing something else. */
function isFixable(err: unknown): boolean {
  return isNumberRefusal(err) || api.errorCode(err) === 'wrong_code';
}

/** Which country a national-format number is read in. Defaults to the org's. */
export function RegionPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select
      label="Country of this number"
      value={value}
      onChange={onChange}
      options={phoneRegionOptions()}
    />
  );
}

/**
 * Enter a mobile → type back the texted code. `orgIds` scopes which of the
 * person's organisations it becomes the work mobile for (default: all).
 *
 * Any failure that is not the person's to fix — no code can be sent here, too
 * many codes, the provider down, offline — offers "Carry on", which saves the
 * number unverified and moves on: the app keeps asking them to confirm it.
 * Three refusals of the number itself (a colleague abroad, say) offer
 * "Carry on" without saving.
 */
export function WorkMobileVerify({
  countryCode = 'AU',
  orgIds,
  initialPhone = '',
  submitLabel = 'Send code',
  verifyAvailable = true,
  role,
  onDone,
}: {
  /** The membership's role, so the hint says why in words that fit it. */
  role?: string | null;
  countryCode?: string;
  orgIds?: string[];
  initialPhone?: string;
  submitLabel?: string;
  verifyAvailable?: boolean;
  /** Finished: verified, saved unverified, or carried on without a number. */
  onDone: (result: { phone: string | null; verified: boolean }) => void;
}) {
  const qc = useQueryClient();
  const [phone, setPhone] = useState(initialPhone);
  const [region, setRegion] = useState(() => knownRegion(countryCode));
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<{ phone: string; display: string } | null>(null);
  const [badNumbers, setBadNumbers] = useState(0);
  const [stuck, setStuck] = useState<string | null>(
    verifyAvailable
      ? null
      : "Verification by text isn't available just now. You can carry on — we'll save the number and ask you to confirm it later.",
  );

  const finish = (result: { phone: string | null; verified: boolean }) => {
    void qc.invalidateQueries({ queryKey: ['me'] });
    onDone(result);
  };

  const onFailure = (err: unknown) => {
    if (isNumberRefusal(err)) setBadNumbers((n) => n + 1);
    if (!isFixable(err)) {
      setStuck(api.errorMessage(err) ?? "We couldn't send a code just now.");
    }
  };

  const start = useMutation({
    mutationFn: () => api.startPhoneVerification(phone.trim(), region),
    onSuccess: (res) => {
      if (!res) return;
      setSent({ phone: res.phone, display: res.display });
      setCode('');
    },
    onError: onFailure,
  });

  const check = useMutation({
    mutationFn: () => api.checkPhoneVerification(sent!.phone, code.trim(), orgIds),
    onSuccess: (res) => {
      if (res) finish({ phone: res.phone, verified: true });
    },
    onError: onFailure,
  });

  const carryOn = useMutation({
    mutationFn: () => api.saveOwnWorkMobile(phone.trim(), region, orgIds),
    onSuccess: (res) => finish({ phone: res?.workMobile.phone ?? null, verified: false }),
  });

  if (stuck) {
    return (
      <div className="space-y-4">
        <Finding>{stuck}</Finding>
        <RegionPicker value={region} onChange={setRegion} />
        <Field
          label="Mobile number"
          value={phone}
          onChange={setPhone}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={phonePlaceholder(region)}
          hint={workMobileHelp(role)}
        />
        {carryOn.isError ? <Finding>{api.errorMessage(carryOn.error)}</Finding> : null}
        <Button
          variant="primary"
          full
          disabled={!phone.trim() || carryOn.isPending}
          onClick={() => carryOn.mutate()}
        >
          {carryOn.isPending ? 'Saving…' : 'Carry on'}
        </Button>
      </div>
    );
  }

  if (!sent) {
    return (
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!phone.trim() || start.isPending) return;
          start.mutate();
        }}
      >
        <RegionPicker value={region} onChange={setRegion} />
        <Field
          label="Mobile number"
          value={phone}
          onChange={setPhone}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={phonePlaceholder(region)}
          hint={workMobileHelp(role)}
        />
        {start.isError && isFixable(start.error) ? (
          <Finding>{api.errorMessage(start.error)}</Finding>
        ) : null}
        <Button type="submit" variant="primary" full disabled={!phone.trim() || start.isPending}>
          {start.isPending ? 'Sending…' : submitLabel}
        </Button>
        {badNumbers >= 3 ? (
          <Button variant="quiet" onClick={() => finish({ phone: null, verified: false })}>
            Carry on without a number for now
          </Button>
        ) : null}
      </form>
    );
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!code.trim() || check.isPending) return;
        check.mutate();
      }}
    >
      <p className="text-[14px] text-[var(--ink-2)]">
        We texted a code to <span className="font-medium text-[var(--ink)]">{sent.display}</span>.
      </p>
      <Field
        label="Code"
        value={code}
        onChange={setCode}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="6 digits"
        autoFocus
      />
      {check.isError && isFixable(check.error) ? (
        <Finding>{api.errorMessage(check.error)}</Finding>
      ) : null}
      {check.isError && api.errorCode(check.error) === 'code_expired' ? (
        <Finding>{api.errorMessage(check.error)}</Finding>
      ) : null}
      <Button type="submit" variant="primary" full disabled={!code.trim() || check.isPending}>
        {check.isPending ? 'Checking…' : 'Verify'}
      </Button>
      <div className="flex flex-wrap gap-2">
        <Button variant="quiet" onClick={() => start.mutate()} disabled={start.isPending}>
          Send a new code
        </Button>
        <Button
          variant="quiet"
          onClick={() => {
            setSent(null);
            setCode('');
            check.reset();
          }}
        >
          Use a different number
        </Button>
      </div>
    </form>
  );
}

/**
 * The requirement, as a banner at the top of the dashboard. Never a wall: it
 * is not shown over the live view, and nothing it does blocks the raise flow,
 * Home's "Waiting for you · Authorise", or any other screen. It opens the
 * verify form in place; "Not now" hides it for this session and it comes back.
 */
export function WorkMobileBanner({ me }: { me: api.Me }) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(workMobileSnoozed);
  if (hidden) return null;

  const owed = me.memberships.filter(
    (m) =>
      m.work_mobile_required &&
      (!m.work_mobile || (me.workMobile?.verifyAvailable && !m.work_mobile.verified)),
  );
  const onFile = owed.find((m) => m.work_mobile)?.work_mobile ?? null;
  const names = owed.map((m) => m.org_name);
  const who = names.length === 1 ? names[0] : 'Your organisations';

  return (
    <section
      aria-label="Your work mobile"
      className="mb-4 rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-[14px] text-[var(--ink)]">
          <span className="font-medium">
            {onFile ? 'Confirm your work mobile.' : 'Add your work mobile.'}
          </span>{' '}
          <span className="text-[var(--ink-2)]">
            {who} needs a number to reach you on. {workMobileWhy(owed[0]?.role)}
          </span>
        </p>
        {!open ? (
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => setOpen(true)}>
              {onFile ? 'Confirm it' : 'Add it'}
            </Button>
            <Button
              variant="quiet"
              onClick={() => {
                snooze();
                setHidden(true);
              }}
            >
              Not now
            </Button>
          </div>
        ) : null}
      </div>
      {open ? (
        <div className="mt-3 max-w-[380px]">
          <WorkMobileVerify
            countryCode={owed[0]?.org_country ?? 'AU'}
            role={owed[0]?.role}
            initialPhone={onFile?.display ?? ''}
            verifyAvailable={me.workMobile?.verifyAvailable !== false}
            onDone={() => setOpen(false)}
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * Whether the banner shows. Pure, so the life-safety exception is a tested
 * rule: never over the live view, never while the server says something is
 * running (`promptNow` false), never for an older server that sent nothing.
 */
export function shouldShowWorkMobileBanner(me: api.Me | null | undefined, route: string): boolean {
  if (!me?.workMobile?.promptNow) return false;
  if (route === 'event') return false;
  return true;
}
